<script setup>
import { computed, onMounted, ref } from "vue";
import apiClient from "@/apiClient";
import TimeAgo from "@/components/TimeAgo.vue";
import { useBreadcrumbs } from "@/composables/useBreadcrumbs";
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  MagnifyingGlassIcon,
} from "@heroicons/vue/24/solid";

const { setBreadcrumb } = useBreadcrumbs();
const loading = ref(false);
const error = ref("");
const logs = ref([]);
const pagination = ref({ current_page: 1, last_page: 1, total: 0 });
const summary = ref({});
const filters = ref({
  search: "",
  request_type: "",
  source: "",
  status: "",
  from: "",
  to: "",
  per_page: 50,
});

const requestTypeLabels = {
  company_profile: "Company profile",
  filing_history: "Filing history",
  document_metadata: "Document metadata",
  document_content: "Document download",
  filing_document_fallback: "Public filing fallback",
};

const sourceLabels = {
  scheduled: "Scheduled check",
  customer_link: "Company link",
  manual_refresh: "Manual refresh",
};

const hasPrevious = computed(() => Number(pagination.value.current_page) > 1);
const hasNext = computed(() => Number(pagination.value.current_page) < Number(pagination.value.last_page));

const loadLogs = async (page = 1) => {
  loading.value = true;
  error.value = "";
  try {
    const params = Object.fromEntries(
      Object.entries({ ...filters.value, page }).filter(([, value]) => value !== "" && value !== null),
    );
    const response = await apiClient.get("/api/admin/companies-house-query-log", { params });
    logs.value = response.data?.logs?.data ?? [];
    pagination.value = response.data?.logs ?? { current_page: 1, last_page: 1, total: 0 };
    summary.value = response.data?.summary ?? {};
  } catch (requestError) {
    error.value = requestError?.response?.status === 403
      ? "Only Super Users can view this query log."
      : "Unable to load the Companies House query log.";
  } finally {
    loading.value = false;
  }
};

const clearFilters = () => {
  filters.value = {
    search: "",
    request_type: "",
    source: "",
    status: "",
    from: "",
    to: "",
    per_page: 50,
  };
  loadLogs(1);
};

const formatDuration = (milliseconds) => {
  if (milliseconds === null || milliseconds === undefined) return "—";
  return Number(milliseconds) >= 1000
    ? `${(Number(milliseconds) / 1000).toFixed(2)} s`
    : `${Number(milliseconds)} ms`;
};

const formatBytes = (bytes) => {
  if (bytes === null || bytes === undefined) return "—";
  if (Number(bytes) < 1024) return `${bytes} B`;
  if (Number(bytes) < 1024 * 1024) return `${(Number(bytes) / 1024).toFixed(1)} KB`;
  return `${(Number(bytes) / (1024 * 1024)).toFixed(1)} MB`;
};

onMounted(() => {
  setBreadcrumb("Companies House Query Log");
  loadLogs();
});
</script>

<template>
  <div class="space-y-5">
    <div class="flex flex-wrap items-start justify-between gap-4">
      <div>
        <p class="text-xs uppercase tracking-[0.25em] text-slate-400">Super User · Audit Log</p>
        <h1 class="text-2xl font-semibold text-slate-900 dark:text-slate-100">Companies House Queries</h1>
        <p class="mt-2 text-sm text-slate-500 dark:text-slate-400">
          Every request Neptune makes to Companies House, including scheduled checks and document retrievals.
        </p>
      </div>
      <button class="btn inline-flex items-center gap-2" type="button" :disabled="loading" @click="loadLogs(pagination.current_page)">
        <ArrowPathIcon class="h-4 w-4" :class="{ 'animate-spin': loading }" /> Refresh
      </button>
    </div>

    <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
      <div class="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <p class="text-xs uppercase tracking-wide text-slate-500">Today</p>
        <p class="mt-2 text-2xl font-semibold text-slate-900 dark:text-slate-100">{{ summary.requests_today ?? 0 }}</p>
        <p class="text-xs text-slate-400">API requests</p>
      </div>
      <div class="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <p class="text-xs uppercase tracking-wide text-slate-500">Last 7 days</p>
        <p class="mt-2 text-2xl font-semibold text-slate-900 dark:text-slate-100">{{ summary.requests_7_days ?? 0 }}</p>
        <p class="text-xs text-slate-400">API requests</p>
      </div>
      <div class="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <p class="text-xs uppercase tracking-wide text-slate-500">Success rate</p>
        <p class="mt-2 text-2xl font-semibold text-emerald-600">{{ summary.success_rate_7_days ?? "—" }}<span v-if="summary.success_rate_7_days !== null && summary.success_rate_7_days !== undefined">%</span></p>
        <p class="text-xs text-slate-400">Last 7 days</p>
      </div>
      <div class="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <p class="text-xs uppercase tracking-wide text-slate-500">Errors</p>
        <p class="mt-2 text-2xl font-semibold" :class="summary.errors_7_days ? 'text-rose-600' : 'text-slate-900 dark:text-slate-100'">{{ summary.errors_7_days ?? 0 }}</p>
        <p class="text-xs text-slate-400">Last 7 days</p>
      </div>
      <div class="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
        <p class="text-xs uppercase tracking-wide text-slate-500">Monitored</p>
        <p class="mt-2 text-2xl font-semibold text-cyan-700 dark:text-cyan-300">{{ summary.monitored_companies ?? 0 }}</p>
        <p class="text-xs text-slate-400">Companies</p>
      </div>
    </div>

    <section class="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <div class="grid gap-3 lg:grid-cols-6">
        <div class="lg:col-span-2">
          <label class="text-xs uppercase tracking-wide text-slate-500">Search</label>
          <div class="relative mt-1">
            <MagnifyingGlassIcon class="pointer-events-none absolute left-3 top-2.5 h-5 w-5 text-slate-400" />
            <input v-model="filters.search" class="input w-full pl-10" placeholder="Customer, company number or error" @keyup.enter="loadLogs(1)" />
          </div>
        </div>
        <div>
          <label class="text-xs uppercase tracking-wide text-slate-500">Request</label>
          <select v-model="filters.request_type" class="input mt-1 w-full">
            <option value="">All requests</option>
            <option v-for="(label, value) in requestTypeLabels" :key="value" :value="value">{{ label }}</option>
          </select>
        </div>
        <div>
          <label class="text-xs uppercase tracking-wide text-slate-500">Trigger</label>
          <select v-model="filters.source" class="input mt-1 w-full">
            <option value="">All triggers</option>
            <option v-for="(label, value) in sourceLabels" :key="value" :value="value">{{ label }}</option>
          </select>
        </div>
        <div>
          <label class="text-xs uppercase tracking-wide text-slate-500">Result</label>
          <select v-model="filters.status" class="input mt-1 w-full">
            <option value="">All results</option>
            <option value="success">Successful</option>
            <option value="error">Errors</option>
          </select>
        </div>
        <div>
          <label class="text-xs uppercase tracking-wide text-slate-500">Rows</label>
          <select v-model="filters.per_page" class="input mt-1 w-full">
            <option :value="25">25</option><option :value="50">50</option><option :value="100">100</option>
          </select>
        </div>
        <div>
          <label class="text-xs uppercase tracking-wide text-slate-500">From</label>
          <input v-model="filters.from" class="input mt-1 w-full" type="date" />
        </div>
        <div>
          <label class="text-xs uppercase tracking-wide text-slate-500">To</label>
          <input v-model="filters.to" class="input mt-1 w-full" type="date" />
        </div>
        <div class="flex items-end gap-2 lg:col-span-4 lg:justify-end">
          <button class="btn accent" type="button" @click="loadLogs(1)">Apply filters</button>
          <button class="btn" type="button" @click="clearFilters">Clear</button>
        </div>
      </div>
    </section>

    <p v-if="error" class="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-950/30 dark:text-rose-300">{{ error }}</p>

    <section class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <div v-if="loading" class="px-6 py-10 text-center text-sm text-slate-500">Loading queries…</div>
      <div v-else-if="!logs.length" class="px-6 py-10 text-center text-sm text-slate-500">No Companies House queries match these filters.</div>
      <div v-else class="overflow-x-auto">
        <table class="min-w-full divide-y divide-slate-200 text-sm dark:divide-slate-700">
          <thead class="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-900/40">
            <tr><th class="px-5 py-3">When</th><th class="px-5 py-3">Company / customer</th><th class="px-5 py-3">Query</th><th class="px-5 py-3">Trigger</th><th class="px-5 py-3">Result</th><th class="px-5 py-3 text-right">Performance</th></tr>
          </thead>
          <tbody class="divide-y divide-slate-100 dark:divide-slate-700">
            <tr v-for="entry in logs" :key="entry.id" class="align-top hover:bg-slate-50/60 dark:hover:bg-slate-900/30">
              <td class="whitespace-nowrap px-5 py-4 text-xs text-slate-500"><TimeAgo :data="entry.attempted_at" /></td>
              <td class="px-5 py-4">
                <p class="font-medium text-slate-900 dark:text-slate-100">{{ entry.company_name || entry.company_number || "Document request" }}</p>
                <p v-if="entry.company_number" class="mt-0.5 text-xs text-slate-400">{{ entry.company_number }}</p>
                <div v-if="entry.customers?.length" class="mt-1 flex flex-wrap gap-x-2 gap-y-1">
                  <router-link v-for="customer in entry.customers" :key="customer.id" :to="`/customer/${customer.id}`" class="text-xs text-cyan-700 hover:underline dark:text-cyan-300">{{ customer.company }}</router-link>
                </div>
              </td>
              <td class="px-5 py-4">
                <p class="font-medium text-slate-800 dark:text-slate-100">{{ requestTypeLabels[entry.request_type] || entry.request_type }}</p>
                <p class="mt-1 max-w-[300px] truncate font-mono text-xs text-slate-400" :title="entry.endpoint">{{ entry.endpoint }}</p>
              </td>
              <td class="px-5 py-4">
                <p class="text-slate-700 dark:text-slate-200">{{ sourceLabels[entry.source] || entry.source }}</p>
                <p v-if="entry.initiated_by" class="mt-1 text-xs text-slate-400">by {{ entry.initiated_by.name }}</p>
              </td>
              <td class="px-5 py-4">
                <span class="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold" :class="entry.successful ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' : 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300'">
                  <CheckCircleIcon v-if="entry.successful" class="h-4 w-4" /><ExclamationCircleIcon v-else class="h-4 w-4" />
                  {{ entry.status_code || "Failed" }}
                </span>
                <p v-if="entry.error_message" class="mt-2 max-w-[280px] text-xs text-rose-600 dark:text-rose-300">{{ entry.error_message }}</p>
              </td>
              <td class="whitespace-nowrap px-5 py-4 text-right text-xs text-slate-500">
                <p>{{ formatDuration(entry.duration_ms) }}</p><p class="mt-1 text-slate-400">{{ formatBytes(entry.response_bytes) }}</p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <div class="flex items-center justify-between text-sm text-slate-500">
      <span>{{ pagination.total ?? 0 }} queries</span>
      <div class="flex items-center gap-2">
        <button class="btn" type="button" :disabled="!hasPrevious || loading" @click="loadLogs(Number(pagination.current_page) - 1)">Previous</button>
        <span>Page {{ pagination.current_page ?? 1 }} of {{ pagination.last_page ?? 1 }}</span>
        <button class="btn" type="button" :disabled="!hasNext || loading" @click="loadLogs(Number(pagination.current_page) + 1)">Next</button>
      </div>
    </div>
  </div>
</template>
